import React, { useState, useCallback, useEffect } from 'react';
import FlightListModal from './FlightListModal';
import HotelListModal from './HotelListModal';
import ActivityListModal from './ActivityListModal';
import TransferListModal from './TransferListModal';
import { getDatePlusDays } from '../../utils/date';
import { TripInfo, Flight, Hotel, Transfer } from '../../types/Quotation';
import { Activity } from '../../types/Activity';
import { QuotationRoomOccupancy, buildDefaultQuotationRooms, normalizeQuotationRooms, toTravellerInfoFromRooms } from './quotationRoomOccupancy';

export const buildFlightTravellers = (
  roomTravellers: TripInfo['travellers'],
  travellers?: TripInfo['travellers']
) => ({
  ...roomTravellers,
  infants: Number(travellers?.infants ?? roomTravellers.infants ?? 0),
});

export interface ModalCallbacks {
  openFlightModal: (isReturn: boolean) => void;
  openHotelModal: (placeIdx: number) => void;
  openActivityModal: (dayIndex: number) => void;
  openTransferModal: (dayIndex: number) => void;
  removeFlight: (dayIndex: number, flightIndex: number) => void;
  removeHotel: (dayIndex: number, hotelIndex: number) => void;
  removeActivity: (dayIndex: number, activityIndex: number) => void;
  removeTransfer: (dayIndex: number, transferIndex: number) => void;
}

interface ModalManagerProps {
  nightsCount: number;
  tripInfo: TripInfo;
  itinerary: any[];
  setItinerary: React.Dispatch<React.SetStateAction<any[]>>;
  startDate: string;
  travellers: TripInfo['travellers'];
  children: (callbacks: ModalCallbacks) => React.ReactNode;
  hotelCache: { data: any[]; loading: boolean; error: any | null }[];
  hotelStartDateOverride?: string;
  hotelMinStars?: number;
  hotelAdultsOverride?: number;
  hotelMealTypeOverride?: string;
  hotelRooms?: QuotationRoomOccupancy[];
  flightReselectTrigger?: number;
  onFlightReselected?: () => void;
}

const ModalManager: React.FC<ModalManagerProps> = ({
  nightsCount,
  tripInfo,
  itinerary,
  setItinerary,
  startDate,
  travellers,
  children,
  hotelCache,
  hotelStartDateOverride,
  hotelMinStars,
  hotelAdultsOverride,
  hotelMealTypeOverride,
  hotelRooms,
  flightReselectTrigger,
  onFlightReselected,
}) => {
  const [showFlightModal, setShowFlightModal] = useState(false);
  const [showHotelModal, setShowHotelModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedFlightDayIndex, setSelectedFlightDayIndex] = useState<number | null>(null);
  const [selectedHotelPlaceIndex, setSelectedHotelPlaceIndex] = useState<number | null>(null);
  const [selectedActivityDayIndex, setSelectedActivityDayIndex] = useState<number | null>(null);
  const [selectedTransferDayIndex, setSelectedTransferDayIndex] = useState<number | null>(null);
  const [flightSelectionMode, setFlightSelectionMode] = useState<'append' | 'reselect'>('append');

  const openFlightModal = useCallback((isReturn: boolean) => {
    setFlightSelectionMode('append');
    setSelectedFlightDayIndex(isReturn ? nightsCount : 0);
    setShowFlightModal(true);
  }, [nightsCount]);

  const addFlightAt = useCallback((dayIndex: number, flight: Flight) => {
    setItinerary(prev =>
      prev.map((item, idx) => (idx !== dayIndex ? item : { ...item, flights: [...item.flights, flight] }))
    );
  }, [setItinerary]);

  const replaceFlightAt = useCallback((dayIndex: number, flight: Flight) => {
    setItinerary((prev) =>
      prev.map((item, idx) => (idx !== dayIndex ? item : { ...item, flights: [flight] }))
    );
  }, [setItinerary]);

  const removeFlight = useCallback((dayIndex: number, flightIndex: number) => {
    setItinerary(prev =>
      prev.map((item, idx) =>
        idx !== dayIndex
          ? item
          : { ...item, flights: item.flights.filter((_:any, i:any) => i !== flightIndex) }
      )
    );
  }, [setItinerary]);

  const removeHotel = useCallback((dayIndex: number, hotelIndex: number) => {
    setItinerary(prev =>
      prev.map((item, idx) =>
        idx !== dayIndex
          ? item
          : { ...item, hotels: item.hotels.filter((_: any, i: any) => i !== hotelIndex) }
      )
    );
  }, [setItinerary]);

  const removeActivity = useCallback((dayIndex: number, activityIndex: number) => {
    setItinerary(prev =>
      prev.map((item, idx) =>
        idx !== dayIndex
          ? item
          : { ...item, activities: item.activities.filter((_:any, i:any) => i !== activityIndex) }
      )
    );
  }, [setItinerary]);

  const removeTransfer = useCallback((dayIndex: number, transferIndex: number) => {
    setItinerary(prev =>
      prev.map((item, idx) =>
        idx !== dayIndex
          ? item
          : { ...item, transfers: (item.transfers || []).filter((_: any, i: any) => i !== transferIndex) }
      )
    );
  }, [setItinerary]);

  const openHotelModal = useCallback((placeIdx: number) => {
    setSelectedHotelPlaceIndex(placeIdx);
    setShowHotelModal(true);
  }, []);

  const openActivityModal = useCallback((dayIndex: number) => {
    setSelectedActivityDayIndex(dayIndex);
    setShowActivityModal(true);
  }, []);

  const openTransferModal = useCallback((dayIndex: number) => {
    setSelectedTransferDayIndex(dayIndex);
    setShowTransferModal(true);
  }, []);

  const hotelBaseDate = hotelStartDateOverride || startDate;
  const roomSelection = normalizeQuotationRooms(hotelRooms || buildDefaultQuotationRooms());
  const roomTravellers = toTravellerInfoFromRooms(roomSelection);
  const flightTravellers = buildFlightTravellers(roomTravellers, travellers);

  useEffect(() => {
    if (!showHotelModal || selectedHotelPlaceIndex === null) return;
    if (tripInfo.places[selectedHotelPlaceIndex]) return;
    setShowHotelModal(false);
    setSelectedHotelPlaceIndex(null);
  }, [selectedHotelPlaceIndex, showHotelModal, tripInfo.places]);

  useEffect(() => {
    if (!flightReselectTrigger || !itinerary.some((day) => (day.flights || []).length > 0)) return;
    setFlightSelectionMode('reselect');
    setSelectedFlightDayIndex(0);
    setShowFlightModal(true);
  }, [flightReselectTrigger, itinerary]);

  const handleSelectHotel = useCallback((hotel: Hotel) => {
    const placeIdx = selectedHotelPlaceIndex ?? 0;
    const place = tripInfo.places[placeIdx];
    if (!place) {
      setShowHotelModal(false);
      setSelectedHotelPlaceIndex(null);
      return;
    }
    // Compute check-in/out dates for this place
    const offsetDays = tripInfo.places
      .slice(0, placeIdx)
      .reduce((sum, p) => sum + p.nights, 0);
    const placeCheckIn = getDatePlusDays(hotelBaseDate, offsetDays);
    const placeCheckOut = getDatePlusDays(placeCheckIn, place.nights);

    const hotelWithDates: Hotel = {
      ...hotel,
      checkInDate: placeCheckIn,
      checkOutDate: placeCheckOut,
    };

    setItinerary(prev =>
      prev.map((item, idx) =>
        idx !== placeIdx
          ? item
          : { ...item, hotels: [hotelWithDates] }
      )
    );
    setShowHotelModal(false);
    setSelectedHotelPlaceIndex(null);
  }, [selectedHotelPlaceIndex, setItinerary, hotelBaseDate, tripInfo.places]);

  const handleSelectActivity = useCallback((activity: Activity) => {
    if (selectedActivityDayIndex === null) return;
    setItinerary(prev =>
      prev.map((item, idx) =>
        idx !== selectedActivityDayIndex
          ? item
          : { ...item, activities: [...item.activities, activity] }
      )
    );
    setShowActivityModal(false);
    setSelectedActivityDayIndex(null);
  }, [selectedActivityDayIndex, setItinerary]);

  return (
    <>
      {children({ openFlightModal, openHotelModal, openActivityModal, openTransferModal, removeFlight, removeHotel, removeActivity, removeTransfer })}

      {showFlightModal && selectedFlightDayIndex !== null && (
        <FlightListModal
          // Prefill onward date as trip start; return date as last day
          startDate={startDate}
          returnDate={getDatePlusDays(startDate, nightsCount)}
          // Prefill airports: from = trip origin, to = first place city resolved by modal
          from={tripInfo.from}
          to={undefined}
          travellers={flightTravellers}
          defaultToCityLabel={tripInfo.places[0]?.label}
          roundTripDefault={true}
          onClose={() => {
            setShowFlightModal(false);
            setSelectedFlightDayIndex(null);
            setFlightSelectionMode('append');
          }}
          onSelectOutbound={(flight) => {
            // Outbound assumed day 0
            if (flightSelectionMode === 'reselect') {
              replaceFlightAt(0, flight);
              return;
            }
            addFlightAt(0, flight);
          }}
          onSelectReturn={(flight) => {
            // Return assumed last day
            if (flightSelectionMode === 'reselect') {
              replaceFlightAt(nightsCount, flight);
              onFlightReselected?.();
              return;
            }
            addFlightAt(nightsCount, flight);
          }}
        />
      )}

      {showHotelModal && selectedHotelPlaceIndex !== null && (() => {
        const place = tripInfo.places[selectedHotelPlaceIndex];
        if (!place) return null;
        const offsetDays = tripInfo.places
          .slice(0, selectedHotelPlaceIndex)
          .reduce((sum, p) => sum + p.nights, 0);
        const placeCheckIn = getDatePlusDays(hotelBaseDate, offsetDays);
        const placeCheckOut = getDatePlusDays(placeCheckIn, place.nights);
        const hotelTravellers = hotelAdultsOverride !== undefined
          ? { ...roomTravellers, adults: hotelAdultsOverride }
          : roomTravellers;
        return (
          <HotelListModal
            place={place}
            checkIn={placeCheckIn}
            checkOut={placeCheckOut}
            travellers={hotelTravellers}
            rooms={roomSelection}
            onClose={() => setShowHotelModal(false)}
            onSelect={handleSelectHotel}
            initialHotels={hotelCache[selectedHotelPlaceIndex]?.data}
            initialLoading={hotelCache[selectedHotelPlaceIndex]?.loading}
            initialError={hotelCache[selectedHotelPlaceIndex]?.error}
            defaultMinStars={hotelMinStars}
            mealTypeOverride={hotelMealTypeOverride}
          />
        );
      })()}

      {showActivityModal && selectedActivityDayIndex !== null && (() => {
        // Map day index to place index by nights (same logic as Transfer modal)
        const placeBoundaries = tripInfo.places.reduce<{ start: number; end: number; idx: number }[]>((acc, p, idx) => {
          const start = acc.length === 0 ? 0 : acc[acc.length - 1].end;
          const end = start + Math.max(0, p.nights || 0);
          acc.push({ start, end, idx });
          return acc;
        }, []);
        const findPlaceIdx = (di: number): number => {
          const hit = placeBoundaries.find(b => di >= b.start && di < b.end);
          if (hit) return hit.idx;
          // If outside ranges (e.g., last departure day), use last place
          return Math.max(0, tripInfo.places.length - 1);
        };
        const placeIdx = findPlaceIdx(selectedActivityDayIndex);
        const placeForDay = tripInfo.places[placeIdx] || { label: 'Unknown', value: '' };
        const activityDate = getDatePlusDays(startDate, selectedActivityDayIndex);
        
        return (
          <ActivityListModal
            dayIndex={selectedActivityDayIndex}
            place={placeForDay}
            travelDate={activityDate}
            onClose={() => {
              setShowActivityModal(false);
              setSelectedActivityDayIndex(null);
            }}
            onSelect={handleSelectActivity}
          />
        );
      })()}

      {showTransferModal && selectedTransferDayIndex !== null && (() => {
        const dayIndex = selectedTransferDayIndex;
        const defaultDate = getDatePlusDays(startDate, dayIndex);
        // Map day index to place index by nights; last (departure) day maps to last place
        const placeBoundaries = tripInfo.places.reduce<{ start: number; end: number; idx: number }[]>((acc, p, idx) => {
          const start = acc.length === 0 ? 0 : acc[acc.length - 1].end;
          const end = start + Math.max(0, p.nights || 0);
          acc.push({ start, end, idx });
          return acc;
        }, []);
        const findPlaceIdx = (di: number): number => {
          const hit = placeBoundaries.find(b => di >= b.start && di < b.end);
          if (hit) return hit.idx;
          // If outside ranges (e.g., last departure day), use last place
          return Math.max(0, tripInfo.places.length - 1);
        };
        const placeIdx = findPlaceIdx(dayIndex);
        const place = tripInfo.places[placeIdx] || { label: 'Unknown', value: '' };
        const hotelsForDay: Hotel[] = itinerary[placeIdx]?.hotels || [];
        const flightsFlat: Flight[] = itinerary.flatMap((d: any) => d.flights || []);
        const uniquePlaces = tripInfo.places.filter((p, idx, arr) => arr.findIndex(q => q.value === p.value) === idx);

        const handleSelectTransfer = (t: Transfer) => {
          setItinerary(prev => prev.map((item, idx) => (
            idx !== dayIndex
              ? item
              : { ...item, transfers: [...(item.transfers || []), t] }
          )));
          setShowTransferModal(false);
          setSelectedTransferDayIndex(null);
        };

        return (
          <TransferListModal
            dayIndex={dayIndex}
            defaultDate={defaultDate}
            defaultPlace={place}
            travellers={travellers}
            placeOptions={uniquePlaces}
            flights={flightsFlat}
            hotelsForDay={hotelsForDay}
            onClose={() => {
              setShowTransferModal(false);
              setSelectedTransferDayIndex(null);
            }}
            onSelect={handleSelectTransfer}
          />
        );
      })()}
    </>
  );
};

export default ModalManager;
